import { useState, useMemo, useCallback, Fragment, useRef } from "react";
import { BudgetRow } from "./types";
import { Check, AlertTriangle, Download, ChevronDown, ChevronRight, Home, Receipt, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, FormSectionHeader } from "@/components";
import { MONTH_RANGE } from "./consts";
import moment from "moment";
import { Button } from "@/components/ui";
interface BudgetTableProps {
  data: BudgetRow[];
  level: "ZONE" | "BLOCK" | "PROPERTY";
  filter: any;
  totalData: any;
}

const TotalRow = ({
  node,
  isZone,
  isBlock,
  isProperty
 }: {
  node: BudgetRow;
  isZone: boolean
  isBlock: boolean;
  isProperty: boolean;
}) => {
  const currentKey = `grandTotal`;
  const isOk = node?.systemNote?.toLowerCase() == "ok";
  const isFully = node?.systemNote?.toLowerCase()?.includes("fully");
  const isVariance = node?.systemNote?.toLowerCase()?.includes("variance");

  return (
    <>
      <Fragment key={currentKey}>
        {/* Parent Row */}
        <tr className="hover:bg-violet-50 bg-violet-50 text-violet-600 font-bold transition-colors">
          {/* Account / Label with Dynamic Hierarchy Indentation */}
          <td className="border text-left p-2">
            GRAND TOTAL
          </td>

          {/* Standard Data Columns */}
          <td className="border text-center p-2">{node?.description || "-"}</td>
          <td className="border text-center p-2">{node?.communityTotal || "0"}</td>

          {/* Community -> Zone */}
          {isProperty || isBlock || isZone ? <>
            <td className="border text-center p-2">{node?.zoneTotal || "0"}</td>
            <td className={cn("border text-center p-2", node?.zoneVariance ? "text-red-600 font-semibold" : "")}>{node?.zoneVariance || "0"}</td>
            <td className="border text-center p-2">{node?.zoneRule || "-"}</td>
          </> : <></>}

          {/* Zone -> Block */}
          {isProperty || isBlock ? <>
            <td className="border text-center p-2">{node?.blockTotal || "0"}</td>
            <td className={cn("border text-center p-2", node?.blockVariance ? "text-red-600 font-semibold" : "")}>{node?.blockVariance || "0"}</td>
            <td className="border text-center p-2">{node?.blockRule || "-"}</td>
          </> : <></>}

          {/* Block -> Property */}
          {isProperty ? <>
            <td className="border text-center p-2">{node?.propertyTotal || "0"}</td>
            <td className={cn("border text-center p-2", node?.propertyVariance ? "text-red-600 font-semibold" : "")}>{node?.propertyVariance || "0"}</td>
            <td className="border text-center p-2">{node?.propertyRule || "-"}</td>
          </> : <></>}

          {/* System Note */}
          <td className={cn("border h-full text-center p-2")}>
            <div className={cn("flex flex-row items-center", node?.systemNote ? "font-semibold" : "", isOk ? "text-green-600" : isFully ? "text-indigo-600" : isVariance ? "text-red-600" : "")}>
              {isFully ? <Receipt className="h-4 stroke-2" /> : isOk ? <Check className="h-4 stroke-2" /> : isVariance ? <AlertTriangle className="h-4 stroke-2"/> : <></>} {node?.systemNote || "-"}
            </div>
          </td>
        </tr>
      </Fragment>
    </>
  )
}

// Sub-component that handles rendering any tree level recursively
const RowTree = ({
  nodes,
  expandedRows,
  onToggle,
  depth = 0,
  path = "",
  isZone,
  isBlock,
  isProperty,
  onGroupGlClick
}: {
  nodes: any[];
  expandedRows: Record<string, boolean>;
  onToggle: (pathKey: string) => void;
  depth?: number;
  path?: string;
  isZone: boolean;
  isBlock: boolean;
  isProperty: boolean;
  onGroupGlClick?: (data: any[]) => void;
}) => {
  if (!nodes || nodes.length === 0) return null;

  return (
    <>
      {nodes.map((node, idx) => {
        const currentKey = path ? `${path}_${idx}` : `${idx}`;
        const hasChildren = !!(node?.children && node.children.length > 0);
        const isExpanded = !!expandedRows[currentKey];
        const isOk = node?.systemNote?.toLowerCase() == "ok";
        const isFully = node?.systemNote?.toLowerCase()?.includes("fully");
        const isVariance = node?.systemNote?.toLowerCase()?.includes("variance");

        return (
          <Fragment key={currentKey}>
            {/* Parent Row */}
            <tr className="hover:bg-slate-50 transition-colors">
              {/* Account / Label with Dynamic Hierarchy Indentation */}
              <td className="border text-left p-2">
                <div
                  className="flex items-center gap-2"
                  style={{ paddingLeft: `${depth * 10}px` }}
                >
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={() => onToggle(currentKey)}
                      className="p-0.5 rounded hover:bg-slate-200 transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-slate-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-600" />
                      )}
                    </button>
                  ) : depth == 4 ? 
                    <div className="bg-blue-50 h-[30px] w-[30px] rounded-sm flex items-center justify-center">
                      <Home className="h-4 stroke-blue-800" />
                    </div> : (
                    <span className="w-4 inline-block" />
                  )}
                  {node?.groupGl ? 
                    <Button variant="link" className={cn(
                      depth === 0
                        ? "font-bold"
                        : depth === 1
                        ? "font-semibold"
                        : "", "p-0")} onClick={() => onGroupGlClick?.(node?.memberGls)}>
                      {node?.label}
                    </Button> :
                    <span
                      className={cn(
                        depth === 0
                          ? "font-bold text-slate-900"
                          : depth === 1
                          ? "font-semibold text-slate-800"
                          : "text-slate-700"
                      )}
                    >
                      {node?.label}
                    </span>}
                </div>
              </td>

              {/* Standard Data Columns */}
              <td className="border text-center p-2 text-slate-600">{node?.description || "-"}</td>
              <td className="border text-center p-2 font-medium">{node?.communityTotal || "0"}</td>

              {/* Community -> Zone */}
              {isProperty || isBlock || isZone ? <>
                <td className="border text-center p-2">{node?.zoneTotal || "0"}</td>
                <td className={cn("border text-center p-2", node?.zoneVariance ? "text-red-600 font-semibold" : "")}>{node?.zoneVariance || "0"}</td>
                <td className="border text-center p-2">{node?.zoneRule || "-"}</td>
              </> : <></>}

              {/* Zone -> Block */}
              {isProperty || isBlock ? <>
                <td className="border text-center p-2">{node?.blockTotal || "0"}</td>
                <td className={cn("border text-center p-2", node?.blockVariance ? "text-red-600 font-semibold" : "")}>{node?.blockVariance || "0"}</td>
                <td className="border text-center p-2">{node?.blockRule || "-"}</td>
              </> : <></>}

              {/* Block -> Property */}
              {isProperty ? <>
                <td className="border text-center p-2">{node?.propertyTotal || "0"}</td>
                <td className={cn("border text-center p-2", node?.propertyVariance ? "text-red-600 font-semibold" : "")}>{node?.propertyVariance || "0"}</td>
                <td className="border text-center p-2">{node?.propertyRule || "-"}</td>
              </> : <></>}

              {/* System Note */}
              <td className={cn("border h-full text-center p-2")}>
                <div className={cn("flex flex-row items-center", node?.systemNote ? "font-semibold" : "", isOk ? "text-green-600" : isFully ? "text-indigo-600" : isVariance ? "text-red-600" : "")}>
                  {isFully ? <Receipt className="h-4 stroke-2" /> : isOk ? <Check className="h-4 stroke-2" /> : isVariance ? <AlertTriangle className="h-4 stroke-2"/> : <></>} {node?.systemNote || "-"}
                </div>
              </td>
            </tr>

            {/* Render children IMMEDIATELY right under this specific parent row */}
            {hasChildren && isExpanded && (
              <RowTree
                nodes={node.children}
                expandedRows={expandedRows}
                onToggle={onToggle}
                depth={depth + 1}
                path={currentKey}
                isZone={isZone}
                isBlock={isBlock}
                isProperty={isProperty}
                onGroupGlClick={onGroupGlClick}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const BudgetReportTable = ({ data, level, filter, totalData }: BudgetTableProps) => {
  const isZone = level == "ZONE";
  const isBlock = level == "BLOCK";
  const isProperty = level == "PROPERTY";
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [openGroupGlDialog, setOpenGroupGlDialog] = useState<boolean>(false);
  const groupGlList = useRef<any[]>([]);

  const onGroupGlClick = (glData: any) => {
    groupGlList.current = glData;
    setOpenGroupGlDialog(true);
  }

  // Individual Row Toggle Handler
  const handleToggle = useCallback((pathKey: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [pathKey]: !prev[pathKey],
    }));
  }, []);

  // Global Expand All Handler
  const handleExpandAll = useCallback(() => {
    const allKeys: Record<string, boolean> = {};

    const traverse = (nodes: any[], currentPath: string) => {
      nodes?.forEach((node, idx) => {
        const key = currentPath ? `${currentPath}_${idx}` : `${idx}`;
        if (node?.children?.length) {
          allKeys[key] = true;
          traverse(node.children, key);
        }
      });
    };

    traverse(data, "");
    setExpandedRows(allKeys);
  }, [data]);

  // Global Collapse All Handler
  const handleCollapseAll = useCallback(() => {
    setExpandedRows({});
  }, []);

  const isAnyExpanded = useMemo(() => Object.values(expandedRows).some(Boolean), [expandedRows]);

  return (
    <>
      <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 p-4 overflow-hidden">
        
        {/* TOP HEADER TOOLBAR */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
          <FormSectionHeader className="text-slate-800" wrapperClassName="mb-0">
            {filter?.year} · {moment(filter?.[MONTH_RANGE]?.from).format("MMM")} - {moment(filter?.[MONTH_RANGE]?.to).format("MMM")} · Actual
          </FormSectionHeader>

          {/* Status Indicators & Action Tools */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1 text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-md">
              <Receipt className="w-3.5 h-3.5" />
              <span>Fully distributed</span>
            </div>
            <div className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md">
              <Check className="w-3.5 h-3.5" />
              <span>OK</span>
            </div>
            <div className="flex items-center gap-1 text-red-600 bg-red-50 px-2.5 py-1 rounded-md">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Variance</span>
            </div>

            <div className="h-4 w-[1px] bg-slate-200" />

            <button className="flex items-center gap-1 px-3 py-1.5 border rounded-md text-slate-600 hover:bg-slate-50 transition-colors">
              <Download className="w-3.5 h-3.5" />
              <span>Export</span>
            </button>

            {/* Expand / Collapse All Toggle Button */}
            {isAnyExpanded ? (
              <button 
                onClick={handleCollapseAll}
                className="flex items-center gap-1 px-3 py-1.5 border rounded-md text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ChevronUp className="w-3.5 h-3.5" />
                <span>Collapse All</span>
              </button>
            ) : (
              <button 
                onClick={handleExpandAll}
                className="flex items-center gap-1 px-3 py-1.5 border rounded-md text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <ChevronDown className="w-3.5 h-3.5" />
                <span>Expand All</span>
              </button>
            )}
          </div>
        </div>

        {/* DATA TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            {/* HEADER GROUPS */}
            <thead>
              <tr>
                <th rowSpan={2} className="border text-center py-5 font-semibold bg-slate-50">ACCOUNT/OBJECT</th>
                <th rowSpan={2} className="border text-center py-5 font-semibold bg-slate-50">DESCRIPTION</th>
                <th className="border text-center font-semibold p-2 bg-slate-50">COMMUNITY TOTAL</th>
                {isProperty || isBlock || isZone ? 
                    <th colSpan={3} className="border text-center font-semibold bg-blue-50 text-blue-800 py-2">COMMUNITY → ZONE</th> : <></>}
                {isProperty || isBlock ? <th colSpan={3} className="border text-center font-semibold bg-purple-50 text-purple-800 py-2">ZONE → BLOCK</th> : <></>}
                {isProperty ? <th colSpan={3} className="border text-center font-semibold bg-green-50 text-green-800 py-2">BLOCK → PROPERTY</th> : <></>}
                    <th rowSpan={2} className="border text-center font-semibold py-5 bg-slate-50">SYSTEM NOTES</th>
              </tr>
              <tr>
                <th className="border text-center font-semibold bg-slate-50 p-2">AMOUNT</th>
                {isProperty || isBlock || isZone ?
                  <>
                    <th className="border text-center font-semibold bg-blue-50 p-2">ZONE TOTAL</th>
                    <th className="border text-center font-semibold bg-blue-50 p-2">VARIANCE</th>
                    <th className="border text-center font-semibold bg-blue-50 p-2">RULE</th>
                  </> : <></>
                }
                
                {isProperty || isBlock ? 
                  <>
                    <th className="border text-center font-semibold bg-purple-50 p-2">BLOCK TOTAL</th>
                    <th className="border text-center font-semibold bg-purple-50 p-2">VARIANCE</th>
                    <th className="border text-center font-semibold bg-purple-50 p-2">RULE</th>
                  </> : <></>
                }
                {isProperty ? 
                  <>
                    <th className="border text-center font-semibold bg-green-50 p-2">PROPERTY TOTAL</th>
                    <th className="border text-center font-semibold bg-green-50 p-2">VARIANCE</th>
                    <th className="border text-center font-semibold bg-green-50 p-2">RULE</th>
                  </> : <></>
                }
              </tr>
            </thead>

            {/* RECURSIVE TABLE BODY */}
            <tbody>
              <RowTree
                nodes={data}
                expandedRows={expandedRows}
                onToggle={handleToggle}
                isZone={isZone}
                isBlock={isBlock}
                isProperty={isProperty}
                onGroupGlClick={onGroupGlClick}
              />
              <TotalRow
                node={totalData}
                isZone={isZone}
                isBlock={isBlock}
                isProperty={isProperty}
              />
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={openGroupGlDialog} setOpen={setOpenGroupGlDialog}
        title={
          <div className="text-2xl font-bold text-primary">
            {"View Group GL(s)"}
            <div className="h-1 w-[30%] bg-primary rounded-full" />
          </div>
        }
      >
        <div className="flex flex-col">
          {groupGlList?.current?.length ? groupGlList?.current?.map((data: any) => {
              return (
                <span>GL - {data}</span>
              )
            }) : <span>No Data Found</span>
          }
        </div>
      </Dialog>
    </>
  );
};

export default BudgetReportTable;
